import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Input, Label, Modal, ModalBody, Row, Spinner } from "@/Components/ui";
;
import {
    API,
    BACKEND_HOST,
    assignReservationOperationalSeat,
    changeReservationItem,
    clearReservationOperationalSeat,
    createPackageOperationalSeat,
    deletePackageOperationalSeat,
    getPackageOperationalSeats,
    updatePackageOperationalSeat,
} from "../api";
import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import moment from "moment";
import Select from "react-select";
import { dialogService } from "@/utils/dialogService";

const SPOT_CHANGE_TAB = "spot-change";
const OPERATIONAL_SEAT_TAB = "operational-seat";
const OPERATIONAL_SEATS_PAGE_SIZE = 100;

const toPositiveInteger = (value) => {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) {
        return 0;
    }
    return Math.max(Math.floor(numberValue), 0);
};

const getNextOperationalSeatName = (usedNames, preferredStart) => {
    let nextNumber = Math.max(toPositiveInteger(preferredStart), 1);
    while (usedNames.has(String(nextNumber))) {
        nextNumber += 1;
    }
    return String(nextNumber);
};

const getOperationalSeatResults = (data) => (Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : []);

const getOperationalSeatCount = (data, fallbackCount) => toPositiveInteger(data?.count ?? data?.total ?? fallbackCount);

const ReservationChangeModal = ({ visible, toggle, targetData, refreshPage }) => {
    const [formData, setFormData] = useState({
        start_date: "",
        paid_amount: 0,
        spot_id: "",
        reserv_state: "",
        phone_num: "",
        package_id: "",
        customer_display_name: "",
    });
    const [spotData, setSpotData] = useState([]);
    const [spotRadioValue, setSpotRadioValue] = useState(targetData ? targetData["자리"] : "");
    const [packageData, setPackageData] = useState([]);
    const [packageId, setPackageId] = useState(targetData ? targetData["package_id"] : null);
    const [showHiddenPackages, setShowHiddenPackages] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [userSearch, setUserSearch] = React.useState("");
    const [userList, setUserlist] = React.useState([]);
    const [isSpotLoading, setIsSpotLoading] = useState(false);
    const [operationalSeats, setOperationalSeats] = useState([]);
    const [operationalSeatTotalCount, setOperationalSeatTotalCount] = useState(0);
    const [selectedOperationalSeatId, setSelectedOperationalSeatId] = useState("");
    const [newOperationalSeatName, setNewOperationalSeatName] = useState("");
    const [operationalSeatLoading, setOperationalSeatLoading] = useState(false);
    const [operationalSeatSaving, setOperationalSeatSaving] = useState(false);
    const [showOperationalSeatManager, setShowOperationalSeatManager] = useState(false);
    const [localAssignedSeat, setLocalAssignedSeat] = useState(targetData?.assigned_seat || null);
    const [activeSeatTab, setActiveSeatTab] = useState(targetData?.["자리"]?.startsWith("virtual") ? OPERATIONAL_SEAT_TAB : SPOT_CHANGE_TAB);
    const assignedSeat = localAssignedSeat;
    const isFreeSeatReservation = Boolean(targetData?.["자리"]?.startsWith("virtual")) || spotData.some((spot) => spot.is_free);
    const selectedPackage = packageData.find((item) => Number(item.id) === Number(packageId));
    const operationalSeatTargetCount = toPositiveInteger(selectedPackage?.count);
    const operationalSeatExistingCount = Math.max(operationalSeatTotalCount, operationalSeats.length);
    const operationalSeatMissingCount = Math.max(operationalSeatTargetCount - operationalSeatExistingCount, 0);
    const activeOperationalSeats = operationalSeats.filter((seat) => seat.is_active);
    const refetchReservations = useCallback(async () => {
        if (refreshPage) {
            await Promise.resolve(refreshPage());
        }
    }, [refreshPage]);
    const loadOperationalSeats = useCallback(async (targetPackageId = packageId) => {
        if (!targetPackageId) {
            setOperationalSeats([]);
            setOperationalSeatTotalCount(0);
            return;
        }
        setOperationalSeatLoading(true);
        try {
            const firstData = await getPackageOperationalSeats(targetPackageId, { page_size: OPERATIONAL_SEATS_PAGE_SIZE });
            let results = getOperationalSeatResults(firstData);
            const totalCount = getOperationalSeatCount(firstData, results.length);
            let page = 2;
            while (results.length < totalCount) {
                const pageData = await getPackageOperationalSeats(targetPackageId, { page, page_size: OPERATIONAL_SEATS_PAGE_SIZE });
                const pageResults = getOperationalSeatResults(pageData);
                if (!pageResults.length) {
                    break;
                }
                results = [...results, ...pageResults];
                page += 1;
            }
            setOperationalSeats(results);
            setOperationalSeatTotalCount(Math.max(totalCount, results.length));
        }
        catch (e) {
            await dialogService.handleAlert(e, { force: "error" });
        }
        finally {
            setOperationalSeatLoading(false);
        }
    }, [packageId]);
    const searchUser = useCallback(() => {
        setSearchLoading(false);
        // console.log("parseInt(userSearch)", parseInt(userSearch))
        if (!userSearch) {
            setUserlist([]);
        }
        else {
            setSearchLoading(true);
            API.get(`${BACKEND_HOST}/accounts/users-crm?search=${userSearch}`)
                .then(async (res) => {
                console.log("search load:", res.data);
                if (res.data?.results)
                    setUserlist(res.data.results);
                else
                    await dialogService.handleAlert("search error...");
                // setUserCount(data.count)
                setSearchLoading(false);
            })
                .catch(async (e) => {
                await dialogService.handleAlert(e, { force: "error" });
                setSearchLoading(false);
            });
        }
    }, [userSearch]);
    const handleSubmit = () => {
        const data = {};
        if (formData.reserv_state)
            data["reserv_state"] = formData.reserv_state;
        if (formData.start_date && targetData["예약시작일"] !== formData.start_date)
            data["start_date"] = formData.start_date;
        if (formData.paid_amount)
            data["paid_amount"] = formData.paid_amount;
        if (spotRadioValue) {
            const spot = spotData.find((item) => item.name === spotRadioValue);
            if (spot)
                data["spot_id"] = spot.id;
        }
        if (formData.phone_num)
            data["phone_num"] = formData.phone_num;
        const originalCustomerDisplayName = targetData?.customer_display_name || "";
        if (formData.customer_display_name !== originalCustomerDisplayName)
            data["customer_display_name"] = formData.customer_display_name;
        console.log("change reservation data", data);
        changeReservationItem(targetData.id, data)
            .then(async (data) => {
            console.log("data", data);
            if (data?.error) {
                await dialogService.handleAlert(data.error);
                return;
            }
            await dialogService.handleAlert("예약정보가 변경되었습니다.");
            toggle();
            refreshPage();
        })
            .catch(async (e) => await dialogService.handleAlert(e, { force: "error" }));
    };
    const handleCreateOperationalSeat = async () => {
        const name = newOperationalSeatName.trim();
        if (!name) {
            await dialogService.handleAlert("자리명을 입력해 주세요.");
            return;
        }
        if (!packageId) {
            await dialogService.handleAlert("예약상품을 선택해 주세요.");
            return;
        }
        setOperationalSeatSaving(true);
        try {
            const created = await createPackageOperationalSeat({
                package: packageId,
                name,
                display_order: operationalSeats.length + 1,
                is_active: true,
            });
            setNewOperationalSeatName("");
            setSelectedOperationalSeatId(String(created.id));
            await loadOperationalSeats(packageId);
        }
        catch (e) {
            await dialogService.handleAlert(e, { force: "error" });
        }
        finally {
            setOperationalSeatSaving(false);
        }
    };
    const handleCreateMissingOperationalSeats = async () => {
        if (!packageId) {
            await dialogService.handleAlert("예약상품을 선택해 주세요.");
            return;
        }
        if (!operationalSeatTargetCount) {
            await dialogService.handleAlert("좌석 개수를 확인할 수 없습니다.");
            return;
        }
        if (operationalSeatMissingCount <= 0) {
            await dialogService.handleAlert("이미 좌석 개수만큼 운영 자리가 등록되어 있습니다.");
            return;
        }
        setOperationalSeatSaving(true);
        try {
            const usedNames = new Set(operationalSeats.map((seat) => String(seat.name)));
            const maxDisplayOrder = operationalSeats.reduce((maxOrder, seat) => Math.max(maxOrder, toPositiveInteger(seat.display_order)), 0);
            let nextDisplayOrder = Math.max(maxDisplayOrder, operationalSeats.length) + 1;
            let lastCreated = null;
            for (let index = 0; index < operationalSeatMissingCount; index += 1) {
                const name = getNextOperationalSeatName(usedNames, nextDisplayOrder);
                usedNames.add(name);
                lastCreated = await createPackageOperationalSeat({
                    package: packageId,
                    name,
                    display_order: nextDisplayOrder,
                    is_active: true,
                });
                nextDisplayOrder += 1;
            }
            if (lastCreated?.id) {
                setSelectedOperationalSeatId(String(lastCreated.id));
            }
            await loadOperationalSeats(packageId);
            await dialogService.handleAlert(`${operationalSeatMissingCount}개의 운영 자리를 생성했습니다.`);
        }
        catch (e) {
            await dialogService.handleAlert(e, { force: "error" });
        }
        finally {
            setOperationalSeatSaving(false);
        }
    };
    const handleAssignOperationalSeat = async () => {
        if (!selectedOperationalSeatId) {
            await dialogService.handleAlert("배정할 자리를 선택해 주세요.");
            return;
        }
        setOperationalSeatSaving(true);
        try {
            const assignment = await assignReservationOperationalSeat(targetData.id, selectedOperationalSeatId);
            const nextAssignedSeat = assignment?.assigned_seat || null;
            setLocalAssignedSeat(nextAssignedSeat);
            await dialogService.handleAlert("운영 자리를 배정했습니다.");
            await loadOperationalSeats(packageId);
            await refetchReservations();
        }
        catch (e) {
            await dialogService.handleAlert(e, { force: "error" });
        }
        finally {
            setOperationalSeatSaving(false);
        }
    };
    const handleClearOperationalSeat = async () => {
        setOperationalSeatSaving(true);
        try {
            await clearReservationOperationalSeat(targetData.id);
            setLocalAssignedSeat(null);
            setSelectedOperationalSeatId("");
            await dialogService.handleAlert("운영 자리 배정을 해제했습니다.");
            await loadOperationalSeats(packageId);
            await refetchReservations();
        }
        catch (e) {
            await dialogService.handleAlert(e, { force: "error" });
        }
        finally {
            setOperationalSeatSaving(false);
        }
    };
    const handleDeactivateOperationalSeat = async (seatId) => {
        setOperationalSeatSaving(true);
        try {
            await updatePackageOperationalSeat(seatId, { is_active: false });
            if (String(seatId) === String(selectedOperationalSeatId)) {
                setSelectedOperationalSeatId("");
            }
            await loadOperationalSeats(packageId);
        }
        catch (e) {
            await dialogService.handleAlert(e, { force: "error" });
        }
        finally {
            setOperationalSeatSaving(false);
        }
    };
    const handleActivateOperationalSeat = async (seatId) => {
        setOperationalSeatSaving(true);
        try {
            await updatePackageOperationalSeat(seatId, { is_active: true });
            await loadOperationalSeats(packageId);
        }
        catch (e) {
            await dialogService.handleAlert(e, { force: "error" });
        }
        finally {
            setOperationalSeatSaving(false);
        }
    };
    const handleDeleteOperationalSeat = async (seat) => {
        if (!await dialogService.confirm(`'${seat.name}' 운영 자리를 삭제하시겠습니까?`)) {
            return;
        }
        setOperationalSeatSaving(true);
        try {
            await deletePackageOperationalSeat(seat.id);
            if (String(seat.id) === String(selectedOperationalSeatId)) {
                setSelectedOperationalSeatId("");
            }
            await loadOperationalSeats(packageId);
        }
        catch (e) {
            await dialogService.handleAlert(e, { force: "error" });
        }
        finally {
            setOperationalSeatSaving(false);
        }
    };
    useEffect(() => {
        setSpotData([]);
        setPackageData([]);
        setSpotRadioValue("");
        setPackageId(targetData ? targetData["package_id"] : null);
      setShowHiddenPackages(false);
        setOperationalSeats([]);
        setOperationalSeatTotalCount(0);
        setLocalAssignedSeat(targetData?.assigned_seat || null);
        setSelectedOperationalSeatId(targetData?.assigned_seat?.id ? String(targetData.assigned_seat.id) : "");
        setNewOperationalSeatName("");
        setShowOperationalSeatManager(false);
        setActiveSeatTab(targetData?.["자리"]?.startsWith("virtual") ? OPERATIONAL_SEAT_TAB : SPOT_CHANGE_TAB);
        setFormData({
            start_date: targetData ? targetData["예약시작일"] : "",
            paid_amount: 0,
            spot_id: "",
            reserv_state: "",
            phone_num: "",
            customer_display_name: targetData?.customer_display_name || "",
        });
    }, [targetData]);
    useEffect(() => {
        if (!packageId || !formData.start_date)
            return;
        let start_date = formData.start_date;
        setIsSpotLoading(true);
      const hiddenQuery = showHiddenPackages ? "&include_hidden=true" : "";
      const url = `${BACKEND_HOST}/package/items/?date=${start_date}${hiddenQuery}`;
        console.log("url", url);
        API.get(url)
            .then(async ({ data }) => {
            if (!data) {
                console.log("data load error");
                return;
            }
            setPackageData(data);
            console.log("data load:", packageId, data);
            const _package = data.find((item) => item.id === Number(packageId));
            // console.log("package", _package);
            if (!_package) {
          setSpotData([]);
          setSpotRadioValue("");
          if (!showHiddenPackages) {
            setShowHiddenPackages(true);
            return;
          }
                await dialogService.handleAlert("해당 상품을 찾을 수 없습니다.");
                return;
            }
            console.log("reason", _package.spots.length ? _package.spots[0].reason : "none");
            const _seatSelectOptions = _package.spots.map((item) => {
                return {
                    id: item.id,
                    value: item.id,
                    name: item.name,
                    is_free: item.is_free,
                    is_able: item.is_able,
                    reason: item.reason,
                    normalPrice: _package.price,
                    membershipPrice: _package.membership_price,
                };
            });
            console.log("seatSelectOptions", _seatSelectOptions);
            setSpotData(_seatSelectOptions);
            setSpotRadioValue((prev) => _seatSelectOptions.some((item) => item.name === prev) ? prev : "");
        })
            .catch(async (e) => await dialogService.handleAlert(e, { force: "error" }))
            .finally(() => setIsSpotLoading(false));
    }, [formData.start_date, packageId, showHiddenPackages]);
    useEffect(() => {
        if (!visible || !packageId) {
            return;
        }
        loadOperationalSeats(packageId);
    }, [visible, packageId, loadOperationalSeats]);
    useEffect(() => {
        if (!isFreeSeatReservation && activeSeatTab === OPERATIONAL_SEAT_TAB) {
            setActiveSeatTab(SPOT_CHANGE_TAB);
        }
    }, [activeSeatTab, isFreeSeatReservation]);
    return (<Modal isOpen={visible} onClose={toggle} centered={true}>
      <ModalBody className="py-3 px-5">
        <div className="mt-2 text-left">
          <div className="mt-4 pt-2 fs-15 mx-sm-5">
            <h4>예약상세</h4>
            {targetData && (<div className="mt-4">
                <p className="text-muted mb-0">상품명: {targetData["예약상품"]}</p>
                <p className="text-muted mb-0">예약일: {targetData["예약시작일"]} 자리: {targetData["자리"]} 번</p>
                {assignedSeat && <p className="text-muted mb-0">배정 자리: {assignedSeat.name}</p>}
                <p className="text-muted mb-0">예약종료일: {targetData["예약종료일"]}</p>
                <p className="text-muted mb-0">예약자: {targetData["예약자"]} {targetData["번호"]}</p>
                <p className="text-muted mb-0">상태: {targetData["예약상태"]} 결제금액: {targetData["결제금액"]}</p>
                {targetData["gifted"] && <p className="text-muted mb-0">선물상태: {targetData["gifted"]}</p>}
                <div className="text-muted mt-2">
                  옵션:{" "}
                  {targetData.options.map((item, idx) => {
                return (<p className="text-muted mx-4 mb-0" key={idx}>
                        <span>{item.name} - </span>
                        <span>{item.price}원</span>
                        <span>&nbsp;{item.state}</span>
                      </p>);
            })}
                </div>
              </div>)}

            <form className="mt-4" onSubmit={(e) => {
            e.preventDefault();
            return;
        }}>

              <div className="nav nav-tabs nav-tabs-custom nav-success mb-3" role="tablist" aria-label="자리 변경 방식">
                {isFreeSeatReservation ? (<button type="button" id="operational-seat-tab" className={activeSeatTab === OPERATIONAL_SEAT_TAB ? "nav-link active" : "nav-link"} role="tab" aria-selected={activeSeatTab === OPERATIONAL_SEAT_TAB} aria-controls="operational-seat-tab-panel" onClick={() => setActiveSeatTab(OPERATIONAL_SEAT_TAB)}>
                    운영 자리 배정
                  </button>) : null}
                <button type="button" id="spot-change-tab" className={activeSeatTab === SPOT_CHANGE_TAB ? "nav-link active" : "nav-link"} role="tab" aria-selected={activeSeatTab === SPOT_CHANGE_TAB} aria-controls="spot-change-tab-panel" onClick={() => setActiveSeatTab(SPOT_CHANGE_TAB)}>
                  자리 변경
                </button>
              </div>

              {activeSeatTab === SPOT_CHANGE_TAB ? (<div id="spot-change-tab-panel" role="tabpanel" aria-labelledby="spot-change-tab" aria-label="자리 변경">
              <Row>
                <Col md={12}>
                  <div className="mb-3">
                    <div className="d-none">
                      <Label htmlFor="formFile" className="form-label">
                        예약상품
                      </Label>
                      <Input type="text" className="form-control" placeholder="" id="reservation-product" value={packageId} maxLength="100" disabled/>
                    </div>
                    <Label htmlFor="formFile" className="form-label">
                      예약상품 변경 ({packageId})
                    </Label>
                    <div className="d-flex justify-content-end mb-2">
                      <label className="form-check-label d-flex align-items-center gap-2" htmlFor="show-hidden-packages-toggle">
                      <input id="show-hidden-packages-toggle" type="checkbox" className="form-check-input mt-0" checked={showHiddenPackages} onChange={(e) => {
                setShowHiddenPackages(e.target.checked);
              }}/>
                      <span>숨김 상품 포함</span>
                      </label>
                    </div>
                    {/* <Input type="text" className="form-control" placeholder="" id="reservation-product" value={reservProduct} onChange={(e) => (setReservProduct(e.target.value))} maxLength="100" disabled /> */}
                    <Select className="mb-0" placeholder="상품 변경..." value={(() => {
            const _value = packageData.find((val) => {
                return packageId === val.id;
            });
            if (!_value)
                return null;
            else
                  return {
                    value: _value.id,
                    label: _value.name,
                  };
        })()} onChange={(val) => {
            setPackageId(val.value);
                setSpotRadioValue("");
        }} options={packageData.map((item) => ({
            value: item.id,
            label: item.name,
        }))} id={"choices-single-reservation-product"}></Select>
                  </div>
                </Col>
              </Row>

              <div className="row">
                <div className="col-md-6">
                  <div className="">
                    <label className="form-label">예약시작일 변경</label>
                    <CommonDatePicker id="date-field" className="form-control" size={"32px"} options={{
        }} placeholder="Select Date" value={formData.start_date} onChange={(value) => {
            setFormData({ ...formData, start_date: moment(value[0]).format("YYYY-MM-DD") });
        }} disabled={formData.end_date && formData.start_date !== formData.end_date}/>
                  </div>
                </div>
                <Col md={4}>
                  <div>
                    <Label htmlFor="formFile" className="form-label">
                      자리 변경
                    </Label>
                    <Input type="text" className="form-control" placeholder="" id="spot" value={spotRadioValue} maxLength="4" disabled/>
                  </div>
                </Col>
                {formData.end_date && formData.start_date !== formData.end_date ? <span className="text-danger">숙박 패키지는 현재 날짜 변경을 할 수 없습니다. 기능 준비중입니다.</span> : null}
              </div>

              <Row className="mb-3">
                <Col md={12}>
                  {isSpotLoading ? (<div>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>) : (<div style={{ marginTop: "27px", display: "flex", flexWrap: "wrap", alignContent: "start", alignItems: "start", justifyItems: "start", justifyContent: "start", gap: "5px" }}>
                      {spotData.map((spot, idx) => {
                return (<Button key={idx} id={`spot-radio-${idx}`} color={spot.is_able ? "primary" : "danger"} outline value={spot.name} onClick={(e) => setSpotRadioValue(e.currentTarget.value)} active={spotRadioValue === spot.name} disabled={!spot.is_able && spot.reason?.includes("reservation")} style={{
                        width: "38px",
                        height: "38px",
                        paidding: "0",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize: "14px",
                    }}>
                            <span>{spot.name}</span>
                          </Button>);
            })}
                    </div>)}
                </Col>
              </Row>
              </div>) : null}

              {isFreeSeatReservation && activeSeatTab === OPERATIONAL_SEAT_TAB ? (<div id="operational-seat-tab-panel" role="tabpanel" aria-labelledby="operational-seat-tab" aria-label="운영 자리 배정">
              <Row className="mb-3">
                <Col md={12}>
                  <div className="border rounded p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Label className="form-label mb-0">운영 자리 배정</Label>
                      {operationalSeatLoading ? <span className="text-muted small">불러오는 중</span> : null}
                    </div>
                    <div className="d-flex gap-2 mb-2">
                      <Select className="flex-grow-1" placeholder="배정할 자리 선택" isDisabled={operationalSeatLoading || operationalSeatSaving} value={(() => {
            const seat = activeOperationalSeats.find((item) => String(item.id) === String(selectedOperationalSeatId));
            return seat ? { value: seat.id, label: seat.name } : null;
        })()} onChange={(val) => setSelectedOperationalSeatId(val ? String(val.value) : "")} options={activeOperationalSeats.map((seat) => ({
            value: seat.id,
            label: seat.name,
        }))}/>
                      <Button type="button" color="primary" disabled={operationalSeatSaving || !selectedOperationalSeatId} onClick={handleAssignOperationalSeat}>
                        배정
                      </Button>
                      <Button type="button" color="light" disabled={operationalSeatSaving || !assignedSeat} onClick={handleClearOperationalSeat}>
                        해제
                      </Button>
                    </div>
                    <div className="d-flex justify-content-end mb-2">
                      <Button type="button" size="sm" color="soft-secondary" onClick={() => setShowOperationalSeatManager((prev) => !prev)}>
                        {showOperationalSeatManager ? "운영 자리 관리 닫기" : "운영 자리 관리 열기"}
                      </Button>
                    </div>
                    {showOperationalSeatManager ? (<>
                      <div className="d-flex justify-content-between align-items-center gap-2 mb-2 flex-wrap">
                        <span className="text-muted small">운영 자리 {operationalSeatExistingCount}개 / 좌석 {operationalSeatTargetCount || "-"}개</span>
                        <Button type="button" size="sm" color="soft-primary" disabled={operationalSeatSaving || operationalSeatLoading || !operationalSeatMissingCount} onClick={handleCreateMissingOperationalSeats}>
                          부족한 자리 생성{operationalSeatMissingCount ? ` (${operationalSeatMissingCount}개)` : ""}
                        </Button>
                      </div>
                      <div className="d-flex gap-2 mb-2">
                        <Input type="text" value={newOperationalSeatName} placeholder="새 자리명" onChange={(e) => setNewOperationalSeatName(e.target.value)} disabled={operationalSeatSaving}/>
                        <Button type="button" color="soft-secondary" disabled={operationalSeatSaving || !newOperationalSeatName.trim()} onClick={handleCreateOperationalSeat}>
                          추가
                        </Button>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {operationalSeats.length ? operationalSeats.map((seat) => (
                          <Button key={seat.id} type="button" size="sm" color={seat.is_active ? "soft-primary" : "soft-secondary"} disabled={operationalSeatSaving} onClick={() => {
                if (seat.is_active) {
                    setSelectedOperationalSeatId(String(seat.id));
                }
            }}>
                            {seat.name}
                            {seat.is_active ? null : " (비활성)"}
                            {seat.is_active ? <span className="ms-2 text-muted" onClick={(event) => {
                event.stopPropagation();
                handleDeactivateOperationalSeat(seat.id);
            }}>비활성</span> : <span className="ms-2 text-primary" onClick={(event) => {
                event.stopPropagation();
                handleActivateOperationalSeat(seat.id);
            }}>활성</span>}
                            <span className="ms-2 text-danger" onClick={(event) => {
                event.stopPropagation();
                handleDeleteOperationalSeat(seat);
            }}>삭제</span>
                          </Button>
                        )) : <span className="text-muted small">등록된 운영 자리가 없습니다.</span>}
                      </div>
                    </>) : null}
                  </div>
                </Col>
              </Row>
              </div>) : null}

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label htmlFor="customer-display-name-input" className="form-label">
                      표시 이름
                    </Label>
                    <Input id="customer-display-name-input" type="text" className="form-control" placeholder="예약 목록에 표시할 이름" value={formData.customer_display_name || ""} maxLength="50" onChange={(e) => {
            setFormData({ ...formData, customer_display_name: e.target.value });
        }}/>
                  </div>
                </Col>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">예약상태</label>
                    <select className="form-select" aria-label="Default select example" onChange={(e) => {
            setFormData({ ...formData, reserv_state: e.target.value });
        }}>
                      <option value="">변경안함</option>
                      <option value="예약">예약</option>
                      <option value="입장">입장</option>
                      <option value="외출">외출</option>
                      <option value="퇴장">퇴장</option>
                      <option value="완료">완료</option>
                      <option value="노쇼">노쇼</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">결제금액</label>
                    <input type="number" className="form-control" placeholder="변경안함" onChange={(e) => {
            setFormData({ ...formData, paid_amount: e.target.value });
        }}/>
                  </div>
                </div>
              </Row>

              <div className="row mb-3">
                <div className="col-md-12 text-start text-danger">
                  * 금액 변경은 데이터만 변경합니다. (토스 환불 X)
                </div>
              </div>

              <Row>
                <Col>
                  <div className="mb-3">
                    <Label htmlFor="phonenumberInput" className="form-label">
                      고객 변경 <span className="text-muted">(** 고객 변경 시 환불 불가)</span>
                    </Label>
                    <Input type="text" className="form-control" placeholder="고객정보를 검색해서 선택해 주세요" id="phoneNum" value={formData.phone_num ? formData.phone_num : ""} onChange={(e) => { }} maxLength="11" disabled/>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
                      <input onChange={(e) => {
            setUserSearch(e.target.value);
        }} id="search-bar-0" type="text" className="form-control search mt-1" style={{ width: "80%" }} placeholder={`고객 변경...`} value={userSearch}/>
                      <Button onClick={() => {
            searchUser();
        }} color="soft-light" className="me-1 text-dark">
                        <i className="bx bx-search-alt search-icon"></i>
                      </Button>
                      <Button onClick={() => {
            setUserSearch("");
            setUserlist([]);
        }} color="soft-secondary" className="me-1">
                        <i className="bx bx-reset"></i>
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  {searchLoading ? (<div style={{}}>
                      <Spinner color="primary"> Searching... </Spinner>
                    </div>) : null}
                  {!!userList &&
            userList.map((user, idx) => {
                if (idx > 4)
                    return null;
                return (<Button className="m-1" key={user.id} color="link" style={{ boxShadow: "none" }} onClick={() => {
                        setFormData(prev => {
                            return {
                                ...prev,
                                phone_num: user["전화번호"],
                            };
                        });
                    }}>
                          <span>
                            {user["이름"]} {user["전화번호"]}
                          </span>
                        </Button>);
            })}
                  <span>{!!userList && userList.length > 5 ? "..." : null}</span>
                </Col>
              </Row>

            </form>
          </div>
        </div>
        <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
          <Button type="submit" onClick={handleSubmit} color="danger">
            변경하기
          </Button>

          <Button type="button" data-bs-dismiss="modal" onClick={() => {
            toggle();
        }} color="light">
            취소
          </Button>
        </div>
      </ModalBody>
    </Modal>);
};
export default ReservationChangeModal;
